/**
 * Utility: safe PDF/blob download via fetch
 * Avoids "body stream already read" by never calling both .json() and .blob() on the same response.
 */
export async function safeDownloadBlob(url, filename, token) {
  const res = await fetch(url, {
    headers: { Authorization: `Bearer ${token}` },
  });

  if (!res.ok) {
    const errText = await res.text();
    let detail = `Error ${res.status}`;
    try {
      detail = JSON.parse(errText).detail || detail;
    } catch {
      if (errText && errText.length < 300) detail = errText;
    }
    throw new Error(detail);
  }

  const blob = await res.blob();
  const blobUrl = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = blobUrl;
  a.download = filename;
  a.rel = "noopener";
  document.body.appendChild(a);
  a.click();
  // Defer revoke so the browser actually starts the download
  setTimeout(() => {
    document.body.removeChild(a);
    URL.revokeObjectURL(blobUrl);
  }, 1500);
}

/**
 * Open a fetched blob in a new tab.
 * Pre-opens the window BEFORE the async fetch (must be inside the user gesture)
 * so the popup blocker doesn't block it in production browsers.
 * Falls back to an anchor-click if window.open is blocked.
 */
export async function safePreviewBlob(url, token) {
  // 1. Open the new tab synchronously (inside the user gesture)
  const newWindow = window.open("about:blank", "_blank", "noopener,noreferrer");

  try {
    const res = await fetch(url, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) {
      const errText = await res.text();
      let detail = `Error ${res.status}`;
      try { detail = JSON.parse(errText).detail || detail; }
      catch { if (errText && errText.length < 300) detail = errText; }
      if (newWindow) newWindow.close();
      throw new Error(detail);
    }
    const blob = await res.blob();
    const blobUrl = URL.createObjectURL(blob);

    if (newWindow && !newWindow.closed) {
      newWindow.location.href = blobUrl;
    } else {
      // Fallback when popup was blocked: anchor click (same tab UX)
      const a = document.createElement("a");
      a.href = blobUrl;
      a.target = "_blank";
      a.rel = "noopener";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    }
    // Give browser time to load the resource before revoking
    setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000);
  } catch (e) {
    if (newWindow && !newWindow.closed) newWindow.close();
    throw e;
  }
}
